import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@cmps/ng-bootstrap';
import { Subscription } from 'rxjs';

import { TranslationKeyMap } from './profile.i18n';

export interface PhotoChangeEvent {
    file: File;
    url: SafeResourceUrl;
}

@Component({
    selector: 'cmps-auth-profile-change-picture',
    templateUrl: './profile-change-picture.component.html',
})
export class ProfileChangePictureComponent {
    i18n = TranslationKeyMap;
    initialPhotoUrl: SafeResourceUrl;
    photo: File;
    photoUrl: SafeResourceUrl;
    photoChange: EventEmitter<PhotoChangeEvent>;

    constructor(
        public activeModal: NgbActiveModal,
        private readonly _sanitizer: DomSanitizer,
    ) {
        this.photoChange = new EventEmitter<PhotoChangeEvent>();
    }

    onPhotoChanged($event): void {
        if (!$event.target.files.length) {
            this.photo = this.photoUrl = null;
            this.photoUrl = this.initialPhotoUrl;
            this.photoChange.emit({ file: this.photo, url: this.photoUrl });
        } else {
            this.photo = $event.target.files[0];
            this.photoUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
                URL.createObjectURL(this.photo),
            );
            this.photoChange.emit({ file: this.photo, url: this.photoUrl });
        }
    }
}

@Component({
    selector: 'cmps-auth-profile-picture-btn',
    template: ` <div class="cmps-profile-picture-change">
        <div
            tabindex="0"
            role="img"
            [attr.aria-labelledby]="ariaLabelledBy"
            [attr.aria-label]="ariaLabel"
        >
            <div class="cmps-profile-picture">
                <img
                    [src]="photoUrl"
                    *ngIf="hasPhotoUrl"
                />
                <cmps-icon
                    class="cmps-cdk-transform-scale-8"
                    svgIcon="account-circle"
                    *ngIf="!hasPhotoUrl"
                ></cmps-icon>
            </div>
            <cmps-wait-status
                [absolute]="true"
                *ngIf="hasPendingPhotoUrl"
            ></cmps-wait-status>
        </div>
        <button
            type="button"
            class="btn-banner"
            (click)="showChangePictureModal()"
            *ngIf="isEditable && !hasPendingPhotoUrl && !hasPhotoUrl"
        >
            <cmps-icon svgIcon="plus-circle"></cmps-icon>
            {{ i18n._694_ADD_A_PHOTO | translate }}
        </button>
        <button
            type="button"
            class="btn-banner"
            (click)="showChangePictureModal()"
            *ngIf="isEditable && !hasPendingPhotoUrl && hasPhotoUrl"
        >
            <cmps-icon svgIcon="camera"></cmps-icon>
            {{ i18n._695_CHANGE_PHOTO | translate }}
        </button>
    </div>`,
    exportAs: 'cmpsProfilePictureBtn',
})
export class ProfilePictureBtn implements OnChanges {
    @Input('editable') isEditable: boolean;
    @Input() photoUrl: SafeResourceUrl | Promise<SafeResourceUrl>;
    @Input() ariaLabelledBy: string;
    @Input() ariaLabel: string;

    @Output() photoUrlChange: EventEmitter<SafeResourceUrl>;
    @Output() modalVisible: EventEmitter<any>;
    @Output() photoChange: EventEmitter<PhotoChangeEvent>;

    ngOnChanges(changes: SimpleChanges): void {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        const change = changes['photoUrl'];
        if (change && change.currentValue instanceof Promise) {
            this.hasPendingPhotoUrl = true;
            change.currentValue.then(
                (photoUrl) => {
                    this.photoUrl = photoUrl;
                    this.photoUrlChange.emit(this.photoUrl);
                    this.hasPendingPhotoUrl = false;
                },
                () => {
                    this.photoUrl = null;
                    this.photoUrlChange.emit(this.photoUrl);
                    this.hasPendingPhotoUrl = false;
                },
            );
        } else {
            // eslint-disable-next-line @typescript-eslint/dot-notation
            this.photoUrl = changes['photoUrl'].currentValue;
        }
    }

    i18n = TranslationKeyMap;
    hasPendingPhotoUrl: boolean;
    private _modal: NgbModalRef;
    private _photoChangeSub: Subscription;
    private _photoData: PhotoChangeEvent;

    get hasPhotoUrl(): boolean {
        return (
            this.photoUrl &&
            typeof this.photoUrl === 'object' &&
            !(this.photoUrl instanceof Promise)
        );
    }

    constructor(private readonly _modalService: NgbModal) {
        this.isEditable = false;
        this.modalVisible = new EventEmitter<{ modal: any }>();
        this.photoUrlChange = new EventEmitter<SafeResourceUrl>();
        this.photoChange = new EventEmitter<PhotoChangeEvent>();
    }

    showChangePictureModal(): void {
        if (!this.isEditable || this.hasPendingPhotoUrl) return;

        this._modal = this._modalService.open(ProfileChangePictureComponent, {
            size: 'lg',
        });

        this._modal.componentInstance.initialPhotoUrl =
            this._modal.componentInstance.photoUrl = !this.hasPhotoUrl
                ? null
                : this.photoUrl;
        // eslint-disable-next-line no-return-assign
        this._photoChangeSub =
            this._modal.componentInstance.photoChange.subscribe(
                (photoData) => (this._photoData = photoData),
            );

        this._modal.result.then(
            (result) => {
                console.log(result);
                this._photoChangeSub.unsubscribe();
                if (result === 'accept') {
                    this.photoUrl = this._photoData.url;
                    this.photoUrlChange.emit(this.photoUrl);
                    this.photoChange.emit(this._photoData);
                }
            },
            (reason) => {
                console.log(reason);
                this._photoChangeSub.unsubscribe();
            },
        );

        this.modalVisible.emit({ modal: this._modal });
    }
}
